import { useEffect } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { NEWS_REFRESH_MS } from "../../../lib/liveNews";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchAllClients } from "../services/clientsService";
import type {
  ClientListStatus,
  ClientRecord,
  ClientTabId,
} from "../types/clients.types";

export type ClientsStatus = "loading" | "ready" | "failed";

export interface ClientsState {
  /** Every client the caller has, in the order the API returned them. */
  clients: ClientRecord[];
  status: ClientsStatus;
  /** What the failed state's retry calls. */
  reload: () => void;
}

const EMPTY: ClientRecord[] = [];

/**
 * Which list each tab on My clients is: the tab is the name the screen gives
 * it, the value is the `status` the endpoint is asked for.
 *
 * The Old tab is `INACTIVE` rather than a filter on `archived_at`, which is
 * what it used to be. The backend's own reading is the better one — a client
 * whose plan has run out belongs in that list whether or not anyone got round
 * to archiving them — and it is the only reading that can tell a client who
 * has never bought anything (`NEW`) from one who has and stopped.
 */
export const LIST_STATUS_OF_TAB: Record<ClientTabId, ClientListStatus> = {
  active: "ACTIVE",
  old: "INACTIVE",
  new: "NEW",
};

const TAB_IDS = Object.keys(LIST_STATUS_OF_TAB) as ClientTabId[];

/**
 * One cache entry per list, so the three tabs do not overwrite each other and
 * a tab already looked at is drawn from cache while its next read runs.
 */
export const clientsQueryKey = (status?: ClientListStatus) =>
  ["clients", "all", status ?? "any"] as const;

/** The unfiltered roster's key — what `useClients` below is cached under. */
export const CLIENTS_QUERY_KEY = clientsQueryKey();

/** What to say when a list never came back. */
const loadFailureMessage = (error: unknown): string =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

/**
 * Every client the caller has, loaded on mount — all of them, or one of the
 * three lists the endpoint filters on when `status` is given.
 *
 * Unfiltered, it is what a screen asking "does this workspace have anyone at
 * all?" wants. Filtered, it is one of My clients' tabs read on its own: the
 * pickers ask for `ACTIVE`, because a plan is written for a client who is on
 * one, not for somebody whose plan has run out or who never bought one. My
 * clients wants all three split, and uses `useClientLists`.
 *
 * Backed by TanStack Query: a screen visited again shows the last list right
 * away while a fresh read runs behind it, so a client added on another screen
 * (or from `AddClientPage` on this one) still shows up within moments rather
 * than making every visit wait on the network. A filtered read is cached under
 * the same key as the tab that shows it, so the two never disagree.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useClients(status?: ClientListStatus): ClientsState {
  const getToken = useTokenGetter();
  const toast = useToast();

  const query = useQuery({
    queryKey: clientsQueryKey(status),
    queryFn: ({ signal }) => fetchAllClients(getToken, { status, signal }),
  });

  // Only fires when there is nothing cached to show in its place — see
  // `useDashboard` for why that is what `isError` means here.
  useEffect(() => {
    if (!query.isError) return;
    console.error("clients failed to load:", query.error);
    toast.error("Could not load your clients", loadFailureMessage(query.error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  return {
    clients: query.data ?? EMPTY,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    reload: () => {
      void query.refetch();
    },
  };
}

/** One tab's list, and where its read got to. */
export interface ClientList {
  clients: ClientRecord[];
  status: ClientsStatus;
}

export interface ClientListsState {
  /** Every tab's list, keyed by the tab that shows it. */
  lists: Record<ClientTabId, ClientList>;
  /** What the failed state's retry calls — every list, not just the open one. */
  reload: () => void;
}

/**
 * The three lists My clients switches between, each one its own request.
 *
 * All three load at once rather than the open tab alone, because the tabs
 * carry counts: a count is an answer, and a tab cannot report one for a list
 * nobody has asked the server about. It is three reads of a third of the
 * roster each, in place of the one walk of all of it this screen used to do.
 *
 * Each list keeps its own status, so a tab whose read failed offers its retry
 * while the two that landed go on showing their rows.
 *
 * All three poll while the screen is open — see the note on `refetchInterval`
 * below, and `liveNews` for why anything carrying news does.
 */
export function useClientLists(): ClientListsState {
  const getToken = useTokenGetter();
  const toast = useToast();

  const results = useQueries({
    queries: TAB_IDS.map((tab) => {
      const status = LIST_STATUS_OF_TAB[tab];
      return {
        queryKey: clientsQueryKey(status),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          fetchAllClients(getToken, { status, signal }),
        // Polled, so a client who lands — or a pre-consultation form that
        // comes back — flags itself on a list somebody is already looking at,
        // rather than waiting for them to reload. `staleTime` is left at zero
        // beside it on purpose: adding a client and coming back to the list
        // still has to show them at once, and that is the mount refetch's
        // doing rather than the poll's. See `liveNews`.
        refetchInterval: NEWS_REFRESH_MS,
      };
    }),
  });

  // One toast however many of the three failed: they fail together when the
  // network is down, and three copies of the same sentence stacked up says
  // nothing the first one did not.
  const anyFailed = results.some((result) => result.isError);

  useEffect(() => {
    if (!anyFailed) return;
    const failure = results.find((result) => result.isError);
    console.error("clients failed to load:", failure?.error);
    toast.error("Could not load your clients", loadFailureMessage(failure?.error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyFailed]);

  // Built fresh each render, but `data` is the same array the cache already
  // held — so the rows a screen memoises off `list.clients` keep their
  // identity between renders.
  const lists = {} as Record<ClientTabId, ClientList>;
  TAB_IDS.forEach((tab, index) => {
    const result = results[index];
    lists[tab] = {
      clients: result.data ?? EMPTY,
      status:
        result.status === "pending"
          ? "loading"
          : result.status === "error"
          ? "failed"
          : "ready",
    };
  });

  return {
    lists,
    reload: () => {
      results.forEach((result) => {
        void result.refetch();
      });
    },
  };
}
